/**
 * Layout Configuration Management
 * Provides utilities for managing layout configurations
 */

import {
  PORTAL_LAYOUT_CONFIGS,
  PORTAL_THEME_CONFIGS,
  DEFAULT_LAYOUT_CONFIG,
  PORTAL_TYPES,
} from '../types/layoutTypes';

/**
 * Get layout configuration for a specific portal
 * @param {string} portalType - The portal type
 * @returns {Object} Layout configuration
 */
export const getLayoutConfig = (portalType) => {
  return PORTAL_LAYOUT_CONFIGS[portalType] || DEFAULT_LAYOUT_CONFIG;
};

/**
 * Get theme configuration for a specific portal
 * @param {string} portalType - The portal type
 * @returns {Object} Theme configuration
 */
export const getThemeConfig = (portalType) => {
  return PORTAL_THEME_CONFIGS[portalType] || PORTAL_THEME_CONFIGS[PORTAL_TYPES.BUREAU];
};

/**
 * Merge custom configuration with portal defaults
 * @param {string} portalType - The portal type
 * @param {Object} customConfig - Custom configuration to merge
 * @returns {Object} Merged configuration
 */
export const mergeLayoutConfig = (portalType, customConfig = {}) => {
  const defaultConfig = getLayoutConfig(portalType);
  return {
    ...defaultConfig,
    ...customConfig,
  };
};

/**
 * Get responsive sidebar configuration optimized for desktop screens
 * @param {Object} config - Layout configuration
 * @param {string} screenSize - Current screen size (laptop, desktop, monitor)
 * @returns {Object} Responsive sidebar configuration
 */
export const getResponsiveSidebarConfig = (config, screenSize = 'desktop') => {
  const responsiveConfig = config.responsive || {};
  const sizeConfig = responsiveConfig[screenSize] || {};

  return {
    width: sizeConfig.sidebarWidth || config.sidebarWidth,
    collapsedWidth: config.sidebarCollapsedWidth,
    floating: config.sidebarFloating || false,
    defaultOpen: config.sidebarDefaultOpen,
    overlay: config.sidebarFloating || false,
    contentPadding: sizeConfig.contentPadding || config.contentPadding,
    headerHeight: sizeConfig.headerHeight || config.headerHeight,
  };
};

/**
 * Determine screen size category for desktop optimization
 * @param {number} width - Screen width in pixels
 * @returns {string} Screen size category
 */
export const getScreenSizeCategory = (width) => {
  if (width >= 1536) return 'monitor'; // 4K monitors and large displays
  if (width >= 1400) return 'desktop'; // Standard desktop monitors
  return 'laptop'; // Laptops and smaller screens
};

/**
 * Get enhanced layout styles with desktop-optimized theming
 * @param {Object} config - Layout configuration
 * @param {Object} theme - MUI theme
 * @param {boolean} isDark - Whether dark mode is active
 * @param {string} screenSize - Current screen size category
 * @returns {Object} Enhanced layout styles
 */
export const getLayoutStyles = (config, theme, isDark = false, screenSize = 'desktop') => {
  const themeConfig = getThemeConfig(config.portalType);
  const responsiveConfig = config.responsive || {};
  const sizeConfig = responsiveConfig[screenSize] || {};

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: isDark
        ? themeConfig.backgroundDark || theme.palette.background.default
        : themeConfig.backgroundLight || theme.palette.grey[50],
      backgroundImage: config.backgroundGradient
        ? isDark
          ? themeConfig.gradientDark
          : themeConfig.gradientLight
        : 'none',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    main: {
      flexGrow: 1,
      width: '100%',
      marginTop: config.showHeader ? `${sizeConfig.headerHeight || config.headerHeight}px` : 0,
      position: 'relative',
      overflow: 'hidden',
      padding: sizeConfig.contentPadding || config.contentPadding,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    glassMorphism: {
      backgroundColor: isDark ? themeConfig.glassMorphismDark : themeConfig.glassMorphismLight,
      backdropFilter: 'blur(12px) saturate(180%)',
      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
      border: `1px solid ${isDark ? themeConfig.borderDark : themeConfig.borderLight}`,
      borderRadius: '12px',
      boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.3)' : '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
    },
    background: config.backgroundGradient
      ? {
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isDark ? themeConfig.gradientDark : themeConfig.gradientLight,
            zIndex: 0,
            opacity: 0.8,
          },
        }
      : {},
  };
};

/**
 * Generate navigation configuration for a portal
 * @param {string} portalType - The portal type
 * @returns {Object} Navigation configuration
 */
export const getNavigationConfig = (portalType) => {
  const config = getLayoutConfig(portalType);

  return {
    showTopNav: config.navigationType.includes('top'),
    showSideNav: config.navigationType.includes('sidebar'),
    sideNavFloating: config.sidebarFloating,
    collapsible: config.sidebarCollapsible,
  };
};
